import React, { useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import classNames from "classnames";
import Downshift from "downshift";

import IconChevron from "components/Icon/Chevron";
import UIInput from "components/UI/Input";
import UIInputSearch from "components/UI/Input/Search";
import UIList from "components/UI/List";
import UICheckbox from "components/UI/Checkbox";
import UIButtonPrimary from "components/UI/Button/Primary";
import MultiDownshift, {
  stateReducer as multipleStateReducer
} from "./MultiDownshift";

export const Container = styled.div.attrs({ className: "dropdown" })`
  position: relative;

  & .chevron {
    height: 8px;
    transition: transform 0.3s;
  }
  &.inline .chevron,
  &.naked .chevron {
    height: 6px;
  }
  &.open .chevron {
    transform: rotate(180deg);
  }

  & .input {
    width: 100%;
  }

  & .dropdown__input,
  & .dropdown__input input {
    cursor: pointer;
  }

  & .list {
    max-height: 218px;
    overflow-y: scroll;
  }

  & .list__item {
    cursor: pointer;
  }

  & .menu {
    border-color: ${props => props.theme.colors.silverAlt};
    box-shadow: 0 0 20px 0 rgba(109, 120, 129, 0.16);
    display: none;
    left: 0;
    overflow-x: hidden;
    position: absolute;
    top: 57px;
    width: 100%;
    z-index: 3;
  }
  &.inline .menu {
    top: 41px;
  }
  &.naked .menu {
    top: 36px;
  }
  &.open .menu {
    display: initial;
  }
`;

const SearchBox = styled(UIList.Item)`
  height: 0;
  opacity: 0;
  padding: 0 15px;

  &.visible {
    height: 52px;
    opacity: 1;
  }

  &:hover {
    background: initial;
    color: initial;
    cursor: initial;
  }
`;

const ApplyButton = styled(UIButtonPrimary)`
  && {
    border-radius: 0;
    width: 100%;
  }
`;

const ALL = { value: "__ALL__", text: "All" };

const renderItem = ({
  index,
  option,
  multiple,
  getItemProps,
  selectedItems,
  highlightedIndex
}) => {
  const content = multiple ? (
    <UICheckbox
      readOnly
      label={option.text}
      checked={selectedItems.map(a => a.value).includes(option.value)}
      labelProps={{ onClick: event => event.preventDefault() }}
    />
  ) : (
    option.content || option.text
  );
  return (
    <UIList.Item
      key={option.value}
      {...getItemProps({
        item: option,
        className: classNames({ active: highlightedIndex === index })
      })}
    >
      {content}
    </UIList.Item>
  );
};

const filterOptions = (search, options, matchStr) => {
  return search
    ? options.filter(option =>
        option.text.toLowerCase().startsWith(matchStr.toLowerCase())
      )
    : options;
};

const stateReducer = (_, changes) => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.mouseUp:
    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem:
    case Downshift.stateChangeTypes.blurInput:
      return {
        ...changes,
        inputValue: ""
      };
    case Downshift.stateChangeTypes.changeInput:
      return {
        ...changes,
        highlightedIndex: 0
      };
    default:
      return changes;
  }
};

const getStateReducer = multiple =>
  multiple
    ? (state, changes) =>
        stateReducer(state, multipleStateReducer(state, changes))
    : stateReducer;

const UIDropdown = ({
  options,
  multiple,
  value,
  search,
  onChange,
  className,
  onStateChange,
  selectedItems,
  initialSelectedItem,
  ...rest
}) => {
  const inputNode = useRef(null);

  options = multiple ? [...options] : options;
  const getOptions = filterOptions.bind(null, search, options);

  const DownshiftComponent = multiple ? MultiDownshift : Downshift;

  const handleStateChange = changes => {
    changes.isOpen && inputNode.current && inputNode.current.focus();
    onStateChange && onStateChange(changes);
  };

  const handleChange = (selectedItem, { selectedItems, setSelectedItems }) => {
    if(!multiple)
      selectedItems = [selectedItem];
    const tempSelectedItem = selectedItem;
    if(selectedItems.filter(a => a.value === selectedItem.value).length > 1){
      selectedItems = selectedItems.filter(item => item.value !== selectedItem.value)
      selectedItem = [];
    }
    const callOnChange = () =>
      onChange && onChange(tempSelectedItem, selectedItems);

    if(!multiple){
      callOnChange();
      return;
    }

    if (selectedItem !== ALL) {
      setSelectedItems(selectedItems, callOnChange);
      return;
    }

    const nextSelectedItems = selectedItems.includes(selectedItem)
      ? options
      : [];
    setSelectedItems(nextSelectedItems, callOnChange);
  };

  return (
    <DownshiftComponent
      onChange={handleChange}
      defaultHighlightedIndex={0}
      onStateChange={handleStateChange}
      stateReducer={getStateReducer(multiple)}
      initialSelectedItem={initialSelectedItem}
      itemToString={item => (item ? item.text : "")}
    >
      {({
        isOpen,
        innerRef,
        closeMenu,
        toggleMenu,
        inputValue,
        getMenuProps,
        getItemProps,
        getRootProps,
        getInputProps,
        selectedItems,
        highlightedIndex
      }) => (
        <Container
          {...rest}
          innerRef={innerRef}
          {...getRootProps({
            refKey: "innerRef",
            className: classNames(className, { multiple, search, open: isOpen })
          })}
        >
          <UIInput
            readOnly
            className={`dropdown__input ${className}`}
            value={value}
            iconRight={<IconChevron className="chevron" />}
            containerProps={{
              onClick: toggleMenu
            }}
          />

          <div {...getMenuProps({ className: "menu" })}>
            <SearchBox className={classNames({ visible: search })}>
              <UIInputSearch
                {...getInputProps({
                  inline: true,
                  ref: inputNode,
                  readOnly: !search
                })}
              />
            </SearchBox>

            <UIList>
              {isOpen
                ? getOptions(inputValue).map((option, index) => 
                    renderItem({
                      option,
                      index,
                      multiple,
                      getItemProps,
                      selectedItems,
                      highlightedIndex
                    })
                  )
                : null}
            </UIList>

            {multiple ? (
              <ApplyButton onClick={closeMenu}>Apply</ApplyButton>
            ) : null}
          </div>
        </Container>
      )}
    </DownshiftComponent>
  );
};

UIDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      content: PropTypes.element
    })
  ),
  multiple: PropTypes.bool,
  search: PropTypes.bool,
  onChange: PropTypes.func,
  onStateChange: PropTypes.func,
  initialSelectedItem: PropTypes.object
};

UIDropdown.defaultProps = {
  options: [],
  multiple: false,
  search: false
};

export default UIDropdown;
